import type { ShipmentsTranslations } from './types'

/**
 * Arabic translations for Shipments domain
 * Comprehensive translations covering all UI text
 */
export const arTranslations: ShipmentsTranslations = {
  // Page/Section level
  title: "الشحنات",
  subtitle: "تتبع وإدارة شحناتك",
  overview: "نظرة عامة",

  // Actions
  addShipment: "إضافة شحنة",
  editShipment: "تعديل الشحنة",
  deleteShipment: "حذف الشحنة",

  // Search and filters
  searchPlaceholder: "البحث في الشحنات...",
  noItemsFound: "لم يتم العثور على شحنات",
  noItemsDescription: "لم تقم بإضافة أي شحنات بعد.",
  addFirst: "أضف شحنتك الأولى",
  noShipments: "لا توجد شحنات",
  noShipmentsDescription: "لا توجد شحنات تطابق التصفية الحالية.",

  // Status labels
  total: "الإجمالي",
  scheduled: "مجدولة",
  loading: "جاري التحميل",
  inTransit: "في الطريق",
  delivered: "تم التسليم",
  cancelled: "ملغاة",
  activeDeliveries: "التسليمات النشطة",

  // Shipment details
  shipmentDetails: "تفاصيل الشحنة",
  shipmentNumber: "رقم الشحنة",
  order: "الطلب",
  quantity: "الكمية",
  productType: "نوع المنتج",
  product: "المنتج",
  origin: "نقطة الانطلاق",
  destination: "الوجهة",
  status: "الحالة",

  // Route info
  routeInfo: "معلومات المسار",
  from: "من",
  to: "إلى",
  eta: "الوصول المتوقع",
  scheduledDate: "تاريخ الجدولة",

  // Transport
  transport: "النقل",
  driver: "السائق",
  truck: "الشاحنة",

  // Weighbridge
  weighbridge: "ميزان الجسر",
  weighbridgeGross: "الوزن الإجمالي",
  weighbridgeTare: "وزن الفارغ",
  weighbridgeNet: "الوزن الصافي",
  weighbridgeTicket: "رقم التذكرة",
  weighbridgeMeasuredAt: "وقت القياس",
  weighbridgeScale: "الميزان",
  trackingTimeline: "الجدول الزمني للتتبع",

  // Form section
  form: {
    name: "اسم الشحنة",
    namePlaceholder: "أدخل اسم الشحنة",
    status: "الحالة",
    validation: {
      nameRequired: "اسم الشحنة مطلوب",
    }
  },

  // Status (compatibility)
  statusLabels: {
    active: "نشط",
    inactive: "غير نشط",
  },

  // Messages
  messages: {
    createSuccess: "تم إنشاء الشحنة بنجاح",
    updateSuccess: "تم تحديث الشحنة بنجاح",
    deleteSuccess: "تم حذف الشحنة بنجاح",
    deleteConfirm: "هل أنت متأكد من حذف هذه الشحنة؟",
  },

  // Table headers
  table: {
    name: "الاسم",
    status: "الحالة",
  },

  // Resource assignment
  resourceUpdated: "تم تحديث المورد بنجاح",
  resourceUpdateFailed: "فشل في تحديث المورد",
  change: "تغيير",
  assign: "تعيين",
  validationError: "خطأ في التحقق",
  noDriverInfo: "لا توجد معلومات السائق",
  waitingForInput: "في انتظار إكمال الإدخال...",

  // Simulation
  simulation: {
    title: "محاكاة الطلب",
    subtitle: "تنفيذ دورة حياة الطلب الكاملة مع استدعاءات API حقيقية",
    orderNumber: "رقم الطلب",
    activityLog: "سجل النشاط",
    inputs: "المدخلات",
    scanIn: "مسح الدخول",
    scanOut: "مسح الخروج",
    recordTare: "تسجيل الوزن الفارغ",
    recordGross: "تسجيل الوزن الإجمالي",
    pullInvoices: "سحب الفواتير",
    entryGate: "بوابة الدخول",
    exitGate: "بوابة الخروج",
    entryFirstWeight: "الوزن الأول عند الدخول (كجم)",
    entrySecondWeight: "الوزن الثاني عند الدخول (كجم)",
    weighbridgeScan: "مسح رمز الميزان",
    scanWeighbridge: "مسح الدخول",
    weighbridgeScanComplete: "اكتمل مسح دخول الميزان",
    tareWeight: "وزن الفارغ (كجم)",
    grossWeight: "الوزن الإجمالي (كجم)",
    invoiceSync: "مزامنة الفواتير",
    waiting: "في الانتظار...",
    assignResource: "تعيين المورد",
    assign: "تعيين",
    joinQueue: "الانضمام إلى القائمة (الكشك)",
    joinQueueButton: "انضم إلى القائمة",
    resourceAssigned: "تم تعيين المورد",
    queueJoined: "تم الانضمام للقائمة · تم إنشاء طلب SAP",
    driverJoinsQueue: "السائق ينضم للقائمة في الكشك · يتم إنشاء طلب مبيعات SAP هنا",
    pullInvoicesDesc: "سحب الفواتير من SAP CPI بعد قبول التسليم",
    licensePlatePlaceholder: "رقم اللوحة",
    driverNamePlaceholder: "اسم السائق",
    orderNumberPlaceholder: "ORD-2026-...",
    gateCodeEntryPlaceholder: "GATE-01",
    gateCodeExitPlaceholder: "GATE-02",
    entryComplete: "اكتمل مسح الدخول",
    exitComplete: "اكتمل مسح الخروج",
    qrScan: "مسح QR",
    licensePlate: "لوحة الترخيص",
    qrReader: "قارئ QR",
    selectQrReader: "اختر قارئ QR...",
    noQrReadersFound: "لا توجد قارئات QR لهذه المنطقة",
    camera: "الكاميرا",
    selectCamera: "اختر الكاميرا...",
    noCamerasFound: "لا توجد كاميرات لهذه المنطقة",
    lpInputPlaceholder: "مثال: 0012-AAA",
    responseError: "الاستجابة (خطأ)",
    responseSuccess: "الاستجابة",
    errors: {
      failedToLoadFactory: "فشل تحميل سياق المصنع",
      failedToLoadLifecycle: "فشل تحميل دورة الحياة",
      noOrderNumber: "رقم الطلب غير محدد",
      orderIdUnavailable: "معرف الطلب غير متاح — أعد تحميل الطلب",
      stepFailed: "فشل تنفيذ الخطوة",
      refreshLifecycleFailed: "فشل تحديث دورة الحياة بعد فشل الخطوة",
    },
  },

  // Shipment card
  noResourceAssigned: "لم يتم تعيين مورد",
  selectResource: "اختر المورد",
  truckIdentification: "تعريف الشاحنة",
  driverCredentials: "بيانات السائق",
  selectedLabel: "محدد",
  plateLettersPlaceholder: "ABC",

  // Top-level convenience keys
  name: "الاسم",
  namePlaceholder: "أدخل اسم الشحنة",
  nameRequired: "اسم الشحنة مطلوب",
  active: "نشط",
  inactive: "غير نشط",
  createSuccess: "تم إنشاء الشحنة بنجاح",
  updateSuccess: "تم تحديث الشحنة بنجاح",
  deleteSuccess: "تم حذف الشحنة بنجاح",
  deleteConfirm: "هل أنت متأكد من حذف هذه الشحنة؟",
}
